import React, { useState, useEffect, useContext, useRef } from 'react';
import { CountContext, ParentContext, CompanyContext, EmployeeContext, StateContext, ActivityContext, CarouselContext } from "../../../layouts/Dashboard/LayoutDashboard";
import { Dropdown, ButtonGroup, FormControl } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './CompaniesNav.css';

const SCROLL_SIZE = 100000; // disable scroll size with this large value
const MIN_SEARCH_TERM_LENGTH = 3;

const CompaniesNav = (classVariant) => {
  // Constants
  const api = process.env.REACT_APP_API_ENDPOINT;
  const activitiesPagedEndpoint = `${api}/v1/activity/paged`;
  const trendGroupEndpoint = `${api}/v1/customers/trendgroups`;
  const getCountsEndpoint = `${api}/v1/customers/counts`;

  // State Management
  const [parentsFiltered, setParentsFiltered] = useState([]);  // displayed parents filtered by search term
  const [companyList, setCompanyList] = useState([]); // this is filtered-by-parent list and is the source of search term filtering
  const [companyFiltered, setCompanyFiltered] = useState([]); // this is the displayed result of search term filtering

  const [selectedParent, setSelectedParent] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [parentSearchTerm, setParentSearchTerm] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");

  // Provider Management
  const { setCompanyCount, setContactCount, setOrderCount, resetCount } = useContext(CountContext);
  const { allParents, setAllParents, selectedParentID, setSelectedParentID } = useContext(ParentContext);
  const { allCompanies, setAllCompanies, selectedCompanyID, setSelectedCompanyID, setSelectedCompanyParentID } = useContext(CompanyContext);
  const { setAllEmployees } = useContext(EmployeeContext);
  const { setAllStates } = useContext(StateContext);
  const {
    setSearchOptions, 
    resetState, 
    resetActivityPage, 
    setResetActivityPage, 
    setResetFilters,
    resetFilters,
    setActivityCount,
    setActivities,
    activityFilters,
    setActivityFilters
  } = useContext(ActivityContext);
  const { currentPage, 
    setCurrentPage, 
    totalPages, 
    setTotalPages, 
    loading, 
    setLoading, 
    totalRecords, 
    setTotalRecords, 
    totalDisplayed, 
    setTotalDisplayed 
  } = useContext(CarouselContext);
  const parentRef = useRef(null);
  const companyRef = useRef(null);
  const [parentOpenToggle, setParentOpenToggle] = useState(false);
  const [companyOpenToggle, setCompanyOpenToggle] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  //List maintenance strategy:
  //the parent and company contexts expose two lists, allParents and allChildren, and the setters
  //external components can add (or remove) values to the lists as needed via the contexts
  //this component will use the global 'all' lists as the source for filtering
  //since the filtered list shown as options is private, when the user opens the dropdown the filtered lists need to be 'rebuilt' from the 'all' lists
  //this will ensure that any additions or deletions to the global 'all' lists will be represented in the displayed filtered lists
  //if we know there were no changes to a global 'all' then the rebuild on dropdown does not need to occur but it's pretty fast
  
  const navigate = useNavigate();

  const getStoredToken = () => {
    return localStorage.getItem('token');
  };

  const storedToken = getStoredToken();

  // Add new state for loading
  const [isLoading, setIsLoading] = useState(false);

  // Fetch Company Parents and Companies on component load
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(trendGroupEndpoint, {
          method: 'GET',
          headers: { Authorization: `Bearer ${storedToken}`, 'Content-Type': 'application/json' },
        });
        if (response.status === 401) {
          console.log("Failed to load Trend Groups");
          navigate('/');
        }
        const data = await response.json();
        setAllParents(data.TrendGroups); // all parents (set in context provider as allParents)
        setParentsFiltered([]); // parents filtered as user enters search term

        setAllCompanies(data.Customers); // all company list (set in context provider to allow access to external components)
        setCompanyList(data.Customers); // companies filtered by parent for any selected parent, used as source for search term filtering
        setCompanyFiltered([]); // companies filtered as user enters search term

        setAllEmployees(data.Employees); // used throughout site via EmployeeContext
        setAllStates(data.States); // used throughout site via StateContext

      } catch (error) {
        console.error('Error in CompaniesNav fetchOptions: ', error);
      }
    };
    // Function to retrieve the JWT from localStorage
    if (storedToken) {
      fetchOptions();
    }
  }, [trendGroupEndpoint]);

  useEffect(() => {
    const fetchCounts = async () => {
      console.log("Fetching counts for CompaniesNav");
      // Skip fetch only when clearing filters AND no company/parent is selected
      if (resetFilters && selectedParentID === 0 && selectedCompanyID === 0) {
        return;
      }
      let identity;
      setIsLoading(true);

      try {
        identity = jwtDecode(storedToken);
      } catch (error) {
        console.error('Error decoding token: ', error);
        return;
      }

      try {
        const fetchData = async (endpoint, payload) => {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${storedToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });
          if (!response.ok) throw new Error(`Error in ${endpoint} fetch`);
          return await response.json();
        };
        
        // Always fetch counts data first
        const countsPayload = { 
          EmployeeUID: identity.sub, 
          ParentCompanyID: selectedParentID, 
          CompanyID: selectedCompanyID 
        };
        const countsData = await fetchData(getCountsEndpoint, countsPayload);
        if (countsData) {
          setCompanyCount(countsData.CompanyCount);
          setContactCount(countsData.ContactCount);
          setOrderCount(countsData.OrderCount);
        }

        // Handle activities count based on selection state
        if (selectedParentID === 0 && selectedCompanyID === 0) {
          // No selections - use counts endpoint activity count
          if (countsData) {
            setActivityCount(countsData.ActivityCount);
            setSearchOptions({ mode: 0, value: 0, data: "" });
          }
        } else {
          // Parent or Company selected - use activities endpoint
          console.log("Fetching activities for CompaniesNav");
          console.log("Selected Parent ID:", selectedParentID);
          console.log("Selected Company ID:", selectedCompanyID);
          const activitiesPayload = {
            EmployeeUID: identity.sub,
            SelectedParentID: selectedParentID,
            SelectedCompanyID: selectedCompanyID,
            PageNo: 1,
            PageSize: 20,
            Filter_ParentID: selectedParentID,
            Filter_CompanyID: selectedCompanyID,
            Filter_ActivityForID: 0,
            Filter_Status: 4,
            Filter_Apply: true,
          };
          const activitiesData = await fetchData(activitiesPagedEndpoint, activitiesPayload);
          if (activitiesData) {
            console.log("Payload Companies:", selectedParentID, selectedCompanyID);
            console.log("Total Company Nav Activities:", activitiesData);
            setSearchOptions({ mode: 2, value: selectedCompanyID, data: "" });
            setActivityCount(activitiesData.TotalCount);
            setActivities(activitiesData.Activities)
            setTotalRecords(activitiesData.TotalCount);
            setTotalPages(Math.ceil(activitiesData.TotalCount / totalDisplayed));
            setCurrentPage(1);
            setTotalDisplayed(Math.min(activitiesData.Activities.length, totalDisplayed));
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error in fetchCounts: ', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounts();

    // Only handle component mounting logic - remove the clearParentDropdown call
    if (!isComponentMounted) {
      setIsComponentMounted(true);
    }
  }, [storedToken, selectedParentID, selectedCompanyID]);
  

  // Initialize selected parent and company if states exist
  useEffect(() => {
    if (selectedParentID) {
      const parent = allParents.find(parent => parent.value === selectedParentID);
      if (parent) {
        selectParent(parent.value, parent.label);
      }
    }
    if (selectedCompanyID) {
      const company = allCompanies.find(company => company.value === selectedCompanyID);
      if (company) {
        selectCompany(company.value, company.label, company.TrendGroupId);
      }
    }
  }, [allParents, allCompanies, selectedParentID, selectedCompanyID]);

  // Focus management on parent search bar
  useEffect(() => {
    if (parentRef && parentRef.current != null) {
      parentRef.current.focus();
    }
  }, [parentOpenToggle]);

  // Focus management on company search bar
  useEffect(() => {
    if (companyRef && companyRef.current != null) {
      companyRef.current.focus();
    }
  }, [companyOpenToggle]);

  const clearParentDropdown = async (e) => {
    console.log("clearParentDropdown");
    if (e) { e.preventDefault(); e.stopPropagation(); }

    setIsLoading(true);

    try {
      // First reset all states atomically
      await Promise.all([
        console.log("Clearing parent states"),
        setParentSearchTerm(""),
        setSelectedParent(""),
        setSelectedParentID(0),
        setSelectedCompany(""),
        setSelectedCompanyID(0),
        setSelectedCompanyParentID(0),
        setParentsFiltered([]),
        resetCount(),
        setSearchOptions({mode: 0, value: 0, data: ""}),
        setResetActivityPage(true),
        setResetFilters(false)
      ]);

      // Only fetch activities if we're actually clearing (not just mounting)
      if (isComponentMounted) {
        const identity = jwtDecode(storedToken);
        const payload = {
          EmployeeUID: identity.sub,
          SelectedParentID: 0,  // Ensure this is 0 when clearing
          SelectedCompanyID: 0,
          PageNo: 1,
          PageSize: 20,
          SortBy: 0,
          SortOrder: 0,
          Filter_ParentID: 0,
          Filter_CompanyID: 0,
          Filter_ActivityForID: -1,
          Filter_Status: 0,
          Filter_Apply: false
        };

        console.log("Clearing with payload:", payload);

        const response = await fetch(activitiesPagedEndpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${storedToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const data = await response.json();
          console.log("Clear response total count:", data.TotalCount);
          setActivityCount(data.TotalCount);
        }
      }
    } catch (error) {
      console.error('Error in clearParentDropdown:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearCompanyDropdown = async (e, preserveParent = false) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }

    setIsLoading(true);

    try {
      // Reset local states
      setCompanySearchTerm("");
      if (selectedParentID === 0) {
        setCompanyFiltered([]); 
      } else {
        setCompanyFiltered(companyList);
      }
      
      // Always reset company-related states
      setSelectedCompanyID(0);
      setSelectedCompanyParentID(0);
      setSelectedCompany("");
      // Reset search options first
      setSearchOptions({mode: 0, value: 0, data: ""});
      
      // Then trigger activity page reset
      setResetActivityPage(true);
      
      // Finally set resetFilters to false since we're handling the reset here
      setResetFilters(false);

      // Always fetch initial activities count
      const identity = jwtDecode(storedToken);
      const payload = {
        EmployeeUID: identity.sub,
        SelectedParentID: preserveParent ? selectedParentID : 0,
        SelectedCompanyID: 0,
        PageNo: 1,
        PageSize: 20,
        SortBy: 0,
        SortOrder: 0,
        Filter_ParentID: preserveParent ? selectedParentID : 0,
        Filter_CompanyID: 0,
        Filter_ActivityForID: -1,
        Filter_Status: 0,
        Filter_Apply: false
      };

      const response = await fetch(activitiesPagedEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Data:", data);
        setActivityCount(data.TotalCount);
      }
    } catch (error) {
      console.error('Error in clearCompanyDropdown:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /// Filter (select) Functions
  const selectParent = (trendGroupId, trendGroupName) => {
    console.log("selectParent", trendGroupId, trendGroupName);
    if (!isComponentMounted) return;

    // Set the state of the company parent dropdown selected
    setSelectedParent(trendGroupName);
    setSelectedParentID(trendGroupId);

    // Filter company list based on parent id
    const companiesFiltered = allCompanies.filter(company =>
      trendGroupId === 0 || company.TrendGroupId === trendGroupId
    );

    // Set the state of the company dropdown list
    setCompanyList(companiesFiltered);

    // Clear the company dropdown but preserve the parent selection
    if (isComponentMounted) {
      clearCompanyDropdown(null, true);
    }

    if (companiesFiltered.length === 1) {
      selectCompany(companiesFiltered[0].value, companiesFiltered[0].label, companiesFiltered[0].TrendGroupId);
    }
  };

  const selectCompany = (companyId, companyName, parentId) => {
    // Called when the user selects a company

    // Set the state of the company dropdown selected
    setSelectedCompanyID(companyId);
    setSelectedCompany(companyName);
    setSelectedCompanyParentID(parentId);
    setSearchOptions({mode: 2, value: companyId, data: ""})
  };

  /// Search Functions
  const handleParentSearch = (search) => {
    const term = search.target.value;
    setParentSearchTerm(term);

    if (term && term.length >= MIN_SEARCH_TERM_LENGTH) {
      const filtered = allParents.filter(option =>
        option.label.toLowerCase().includes(term.toLowerCase())
      );
      setParentsFiltered(filtered.slice(0, SCROLL_SIZE));
    } else {
      setParentsFiltered([]);
    }
  };

  const handleCompanySearch = (search) => {
    const term = search.target.value;

    setCompanySearchTerm(term);
    if (selectedParent || term.length >= MIN_SEARCH_TERM_LENGTH) {
      const filtered = companyList.filter(option =>
        option.label.toLowerCase().includes(term.toLowerCase())
      );
      setCompanyFiltered(filtered.slice(0, SCROLL_SIZE));
    } else {
      setCompanyFiltered([]);
    }
  };

  // Dropdown toggled Functions
  const parentToggled = (isOpening) => {
    // Rebuild filtered list based on allParents list (which may have changed)
    if (isOpening) {
      // Filter based on search term
      if (parentSearchTerm && parentSearchTerm.length >= MIN_SEARCH_TERM_LENGTH) {
        const filtered = allParents.filter(option =>
          option.label.toLowerCase().includes(parentSearchTerm.toLowerCase())
        );
        setParentsFiltered(filtered.slice(0, SCROLL_SIZE));
      } else {
        setParentsFiltered([]);
      }
      setParentOpenToggle(!parentOpenToggle);
    }
  };

  const companyToggled = (isOpening) => {
    // Rebuild filtered lists based on allCompanies list (which may have changed)
    if (isOpening) {
      // Rebuild filtered list based on search term
      const companiesFiltered = allCompanies.filter(company =>
        selectedParentID === 0 || company.TrendGroupId === selectedParentID
      );
      setCompanyList(companiesFiltered); // List of companies filtered by parent

      // Filter based on search term
      if (selectedParent || companySearchTerm.length >= MIN_SEARCH_TERM_LENGTH) {
        const filtered = companiesFiltered.filter(option =>
          option.label.toLowerCase().includes(companySearchTerm.toLowerCase())
        );
        setCompanyFiltered(filtered.slice(0, SCROLL_SIZE));
      } else {
        setCompanyFiltered([]);
      }

      setCompanyOpenToggle(!companyOpenToggle);
    }
  };

  // Add this useEffect to properly see state updates
  useEffect(() => {
    console.log("selectedParentID updated:", selectedParentID);
  }, [selectedParentID]);

  return (
    <>
      <Dropdown as={ButtonGroup} className="company-dropdown" onToggle={parentToggled}>
        <Dropdown.Toggle variant={classVariant.dropDownClass} id="dropdown-basic" >
          <div className="dropdown-container">
            <div className="dropdown-title">Company Parent</div>
            <div className="dropdown-value">{selectedParent ? selectedParent : "Select..."}</div>
            <div className="dropdown-clear">
              <button 
                className="btn-clear" 
                onClick={(e) => clearParentDropdown(e)} 
                disabled={isLoading}
              >
                <i className="fa fa-close" />
              </button>
            </div>
            <span className="dropdown-line"></span>
            <div className="dropdown-caret"><i className="fa fa-chevron-down" /></div>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu variant={classVariant.dropDownClass}>
          <FormControl
            ref={parentRef}
            id="parentInput"
            type="text"
            placeholder="Search..."
            value={parentSearchTerm}
            onChange={handleParentSearch}
          />
          <Dropdown.Divider />
          <div className="scrollContainer">
            {parentsFiltered.length !== 0 ? parentsFiltered.map((option) => (
              <Dropdown.Item eventKey={option.value} key={option.value} onClick={() => selectParent(option.value, option.label)} href="#">{option.label}</Dropdown.Item>
            )) : null}
          </div>
        </Dropdown.Menu>
      </Dropdown>

      <Dropdown onToggle={companyToggled} className="company-dropdown">
        <Dropdown.Toggle variant={classVariant.dropDownClass} id="dropdown-basic">
          <div className="dropdown-container">
            <div className='dropdown-title'>Company</div>
            <div className='dropdown-value'>{selectedCompany ? selectedCompany : "Select..."}</div>
            <div className="dropdown-clear">
              <button 
                className="btn-clear" 
                onClick={(e) => clearCompanyDropdown(e)} 
                disabled={isLoading}
              >
                <i className="fa fa-close" />
              </button>
            </div>
            <span className="dropdown-line"></span>
            <div className="dropdown-caret"><i className="fa fa-chevron-down" /></div>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu variant={classVariant.dropDownClass}>
          <FormControl
            ref={companyRef}
            type='text'
            placeholder='Search...'
            value={companySearchTerm}
            onChange={handleCompanySearch}
          />
          <Dropdown.Divider />
          <div className="scrollContainer">
            {companyFiltered.length !== 0 ? companyFiltered.map((option) => (
              <Dropdown.Item eventKey={option.value} key={option.value} onClick={() => selectCompany(option.value, option.label, option.TrendGroupId)} href="#">{option.label}</Dropdown.Item>
            )) : null}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default CompaniesNav;